/**
 * El ÚNICO módulo que habla con la API.
 *
 * Concentra tres cosas que si se dispersan se rompen: la URL base, el envío de
 * la cookie de sesión, y el desempaquetado del sobre de error de §5. Un `fetch`
 * suelto en un componente se olvidará de al menos una de las tres.
 */

import { registrarSenal } from "@/lib/bitacora"
import { sesionRechazada } from "@/lib/cambioDeSesion"
import { resumirValidacion } from "@/lib/campos"

/** Conjunto CERRADO de códigos (§5). Cualquier otro es un defecto del backend. */
export type CodigoError =
  | "NO_AUTENTICADO"
  | "SIN_PERMISO"
  | "NO_ENCONTRADO"
  | "VALIDACION"
  | "TRANSICION_INVALIDA"
  | "DOCUMENTOS_FALTANTES"
  | "CONFLICTO"
  | "ARCHIVO_INVALIDO"
  | "DEMASIADAS_PETICIONES"
  // Los tres que añade v2. Los emite el backend —`SaldoInsuficiente`,
  // `MontoExcedeSaldo` y `TasaNoDisponible`— y son 409 y no 404 a propósito: el
  // recurso existe y la petición es válida, lo que falta es un dato o un saldo. Sin
  // ellos aquí, una pantalla no puede distinguir «te pasaste del saldo» de un
  // CONFLICTO cualquiera, y el operador lee un mensaje genérico sobre dinero.
  | "SALDO_INSUFICIENTE"
  | "MONTO_EXCEDE_SALDO"
  | "TASA_NO_DISPONIBLE"

export interface SobreError {
  error: {
    code: CodigoError
    message: string
    details: Record<string, unknown> | null
  }
}

export class ErrorApi extends Error {
  constructor(
    readonly codigo: CodigoError,
    mensaje: string,
    readonly estado: number,
    readonly detalles: Record<string, unknown> | null = null,
  ) {
    super(mensaje)
    this.name = "ErrorApi"
  }
}

/**
 * Base vacía a propósito: en desarrollo el proxy de Vite manda `/api` al backend,
 * y en producción Traefik sirve ambos desde el mismo origen. Así la MISMA
 * compilación vale para los dos sitios — con una URL absoluta habría que
 * recompilar por entorno.
 */
const BASE = ""

export async function pedir<T>(ruta: string, opciones: RequestInit = {}): Promise<T> {
  const metodo = opciones.method ?? "GET"
  let respuesta: Response
  try {
    respuesta = await fetch(`${BASE}${ruta}`, {
      ...opciones,
      // La sesión va en cookie HttpOnly: sin esto no viaja y todo da 401.
      credentials: "include",
      headers: {
        ...(opciones.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
        ...opciones.headers,
      },
    })
  } catch (fallo) {
    // ⚠ **Una petición que no llega a recibir respuesta se anota y se relanza.** El
    // servidor caído y el wifi de quien reporta se ven igual desde aquí, y por eso
    // el clasificador la deja en INDETERMINADO en vez de inventarse cuál fue.
    registrarSenal({
      clase: "RED",
      metodo,
      ruta,
      mensaje: fallo instanceof Error ? fallo.message : String(fallo),
    })
    throw fallo
  }

  if (respuesta.status === 204) return undefined as T

  const cuerpo = await respuesta.json().catch(() => null)

  if (!respuesta.ok) {
    // La sesión murió en el servidor: se suelta también aquí (`lib/cambioDeSesion.ts`).
    if (respuesta.status === 401) sesionRechazada(ruta)
    const sobre = cuerpo as SobreError | null
    const detalles = sobre?.error?.details ?? null
    // ⚠ El titular de un 422 es «Datos de entrada invalidos», que no es un mensaje
    // sino un acertijo: con siete campos en el formulario deja a quien lo rellenó
    // buscando a ojo cuál está mal. El servidor SÍ dice cuál y por qué —§5 lo manda
    // en `details.campos`— y hasta ahora se tiraba. Se compone aquí, que es el único
    // sitio que desempaqueta el sobre, y así lo ganan de golpe las treinta y cinco
    // pantallas que enseñan `error.message` sin saber nada de esto.
    const resumen = sobre?.error?.code === "VALIDACION" ? resumirValidacion(detalles) : null
    // ⚠ **Aquí y no en cada pantalla.** Éste es el único módulo que desempaqueta el
    // sobre, así que es el único sitio donde el código y el estado existen juntos.
    // Anotarlo en los componentes exigiría acordarse en las treinta y cinco que
    // pintan un error, y bastaría con una que se olvidara.
    registrarSenal({
      clase: "API",
      metodo,
      ruta,
      estado: respuesta.status,
      codigo: sobre?.error?.code,
      mensaje: sobre?.error?.message,
      detalles,
    })
    throw new ErrorApi(
      sobre?.error?.code ?? "CONFLICTO",
      resumen ?? sobre?.error?.message ?? `Error ${respuesta.status}`,
      respuesta.status,
      detalles,
    )
  }

  // ⚠ Un 2xx cuyo cuerpo no es JSON es un DEFECTO, y se lanza en vez de devolver
  // `null`. Devolverlo era la forma de fallar más cara que tuvo este cliente: con
  // una ruta sin el prefijo `/api/v1`, el servidor de Vite responde el `index.html`
  // del SPA con un 200 perfectamente sano, `json()` revienta, esto devolvía `null`,
  // y la pantalla caía con «Cannot read properties of null (reading 'map')» — un
  // mensaje que no nombra la URL y manda a depurar el componente, que estaba bien.
  // Lanzar aquí pone la ruta en el mensaje y lo convierte en el `isError` que cada
  // pantalla ya sabe pintar.
  if (cuerpo === null) {
    registrarSenal({ clase: "API", metodo, ruta, estado: respuesta.status })
    throw new ErrorApi(
      "CONFLICTO",
      `La respuesta de ${ruta} no es JSON (estado ${respuesta.status}). ` +
        "Si la ruta no empieza por /api/v1/, el proxy de desarrollo devuelve el SPA.",
      respuesta.status,
    )
  }

  return cuerpo as T
}

/**
 * Una respuesta BINARIA: una imagen, un PDF.
 *
 * ⚠ **Aparte de `pedir` y no un parámetro suyo.** Aquélla siempre hace `.json()` —y
 * su aviso sobre «la respuesta no es JSON» es una de sus mejores partes—, así que
 * meterle un modo crudo la obligaría a bifurcar justo en la línea que existe para no
 * bifurcar. Aquí el cuerpo no se lee como JSON nunca.
 *
 * ⚠ **El error SÍ viene en JSON**, porque el manejador global del servidor devuelve
 * el sobre de §5 también en las rutas que sirven archivos. Se desempaqueta igual, y
 * por eso un 404 de «esa ficha no tiene foto» llega con su mensaje y no como un
 * `blob` vacío que la pantalla pintaría como una imagen rota.
 */
export async function pedirBinario(ruta: string): Promise<Blob> {
  const respuesta = await fetch(`${BASE}${ruta}`, { credentials: "include" })
  if (!respuesta.ok) {
    if (respuesta.status === 401) sesionRechazada(ruta)
    const sobre = (await respuesta.json().catch(() => null)) as SobreError | null
    throw new ErrorApi(
      sobre?.error?.code ?? "CONFLICTO",
      sobre?.error?.message ?? `Error ${respuesta.status}`,
      respuesta.status,
      sobre?.error?.details ?? null,
    )
  }
  return respuesta.blob()
}

/**
 * Un POST con cuerpo JSON cuya respuesta es un ARCHIVO: la exportación a Excel (29/10).
 * El error viene en el sobre de §5, como en `pedirBinario`.
 */
export async function enviarParaBinario(ruta: string, datos: unknown): Promise<Blob> {
  const respuesta = await fetch(`${BASE}${ruta}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(datos),
  })
  if (!respuesta.ok) {
    if (respuesta.status === 401) sesionRechazada(ruta)
    const sobre = (await respuesta.json().catch(() => null)) as SobreError | null
    throw new ErrorApi(
      sobre?.error?.code ?? "CONFLICTO",
      sobre?.error?.message ?? `Error ${respuesta.status}`,
      respuesta.status,
      sobre?.error?.details ?? null,
    )
  }
  return respuesta.blob()
}

/**
 * El cuerpo de la petición: JSON, salvo que ya sea un formulario.
 *
 * ⚠ **Un `FormData` NO se serializa.** `JSON.stringify(new FormData())` da `"{}"` —no
 * falla, no avisa: manda un objeto vacío—, y el servidor contesta que faltan los
 * campos que se acaban de rellenar. Pasó con el soporte de un movimiento y estaba
 * igual de roto en ejecutar un pago y en adjuntar la factura de una orden, que son las
 * otras dos rutas multiparte. `fetch` le pone solo el `Content-Type` con su `boundary`,
 * que es justo lo que no se puede escribir a mano.
 */
function cuerpo(datos: unknown): BodyInit | undefined {
  if (datos === undefined) return undefined
  return datos instanceof FormData ? datos : JSON.stringify(datos)
}

export const api = {
  get: <T>(ruta: string) => pedir<T>(ruta),
  post: <T>(ruta: string, datos?: unknown) =>
    pedir<T>(ruta, { method: "POST", body: cuerpo(datos) }),
  patch: <T>(ruta: string, datos: unknown) =>
    pedir<T>(ruta, { method: "PATCH", body: cuerpo(datos) }),
  /**
   * Reemplaza un recurso entero.
   *
   * ⚠ **No es un `patch` con otro nombre.** `PATCH` manda lo que cambió; `PUT`
   * manda el estado completo y el servidor descarta lo anterior. El reparto de
   * permisos de un rol es lo segundo: mandando la diferencia habría que calcularla
   * aquí, y dos pestañas abiertas sobre el mismo rol la aplicarían contra un estado
   * que ya cambió.
   */
  put: <T>(ruta: string, datos: unknown) => pedir<T>(ruta, { method: "PUT", body: cuerpo(datos) }),
  delete: <T>(ruta: string) => pedir<T>(ruta, { method: "DELETE" }),
}
